import { useState, useEffect, useRef } from 'react';
import { PageHeader } from '@/components/composites/PageHeader';
import { Button } from '@/components/primitives/Button';
import { Select } from '@/components/primitives/Select';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ProjectBatchStatusList } from '@/components/composites/ProjectBatchStatusList';
import { ReclamationSuggestionsPanel } from '@/components/composites/ReclamationSuggestionsPanel';
import { useEmployees } from '@/hooks/useEmployees';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import {
  useDayDiary,
  useDayDiaryV2,
  type DayDiaryData,
  type DayDiaryEntry,
  type DiarySummary,
  type DiarySummaryBlock,
  type AttributedEventItem,
  type UnattributedEventItem,
} from '@/hooks/useDayDiary';
import { formatDayFull, currentDate } from '@/lib/dates';
import styles from './DayDiaryPage.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayIso(): string {
  return currentDate().toISOString().slice(0, 10);
}

function fmtMinutes(n: number): string {
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatTimeRange(startsAt: string, endsAt: string): string {
  return `${startsAt.slice(11, 16)} – ${endsAt.slice(11, 16)} UTC`;
}

function formatDuration(startsAt: string, endsAt: string): string {
  const ms = Date.parse(endsAt) - Date.parse(startsAt);
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function countDistinctCategories(eventItems: AttributedEventItem[]): number {
  const seen = new Set<string>();
  for (const ev of eventItems) {
    seen.add(`${ev.classifiedClientName ?? ''}/${ev.classifiedProjectName ?? ''}`);
  }
  return seen.size;
}

// ── App breakdown helpers (task 220) ─────────────────────────────────────────

interface CategoryLabel {
  client: string | null;
  project: string | null;
}

interface AppTitleGroup {
  app: string;
  titles: { title: string; count: number; categories: CategoryLabel[] }[];
  total: number;
}

function groupEventItems(items: AttributedEventItem[]): AppTitleGroup[] {
  const appMap = new Map<string, Map<string, AttributedEventItem[]>>();
  for (const ev of items) {
    const app = ev.app ?? 'Unknown';
    const label = ev.title ?? ev.site ?? '—';
    if (!appMap.has(app)) appMap.set(app, new Map());
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- appMap.has(app) guarantees presence
    const tmap = appMap.get(app)!;
    if (!tmap.has(label)) tmap.set(label, []);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- tmap.has(label) guarantees presence
    tmap.get(label)!.push(ev);
  }
  return Array.from(appMap.entries())
    .map(([app, tmap]) => {
      let total = 0;
      const titles = Array.from(tmap.entries())
        .map(([title, evs]) => {
          total += evs.length;
          const catMap = new Map<string, CategoryLabel>();
          for (const ev of evs) {
            const key = `${ev.classifiedClientName ?? ''}/${ev.classifiedProjectName ?? ''}`;
            if (!catMap.has(key))
              catMap.set(key, {
                client: ev.classifiedClientName,
                project: ev.classifiedProjectName,
              });
          }
          const categories = Array.from(catMap.values()).filter(
            (c) => c.client !== null || c.project !== null,
          );
          return { title, count: evs.length, categories };
        })
        .sort((a, b) => b.count - a.count);
      return { app, titles, total };
    })
    .sort((a, b) => b.total - a.total);
}

function buildAppBreakdown(entries: DayDiaryEntry[], entryIds: string[]): AppTitleGroup[] {
  const allEvents = entries
    .filter((e) => entryIds.includes(e.id))
    .flatMap((e) => e.eventItems ?? []);
  return groupEventItems(allEvents);
}

function csvCell(value: string | null | undefined): string {
  const s = value ?? '';
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportToCsv(diary: DayDiaryData) {
  const COLS = [
    'section',
    'entry_title',
    'entry_description',
    'entry_starts_at',
    'entry_ends_at',
    'entry_duration',
    'entry_client',
    'entry_project',
    'entry_confidence',
    'entry_event_count',
    'entry_distinct_categories',
    'event_time',
    'event_app',
    'event_window_title',
    'event_site',
    'current_client',
    'current_project',
  ];

  const rows: string[][] = [COLS];

  for (const entry of diary.entries) {
    rows.push([
      'Diary Entry',
      entry.title,
      entry.description,
      entry.startsAt.slice(0, 16).replace('T', ' '),
      entry.endsAt.slice(0, 16).replace('T', ' '),
      formatDuration(entry.startsAt, entry.endsAt),
      entry.clientName ?? '',
      entry.projectName ?? '',
      String(Math.round(entry.confidence * 100) / 100),
      String(entry.eventCount),
      String(countDistinctCategories(entry.eventItems ?? [])),
      '',
      '',
      '',
      '',
      '',
      '',
    ]);
    for (const ev of entry.eventItems ?? []) {
      rows.push([
        'Attributed Event',
        entry.title,
        '',
        '',
        '',
        '',
        entry.clientName ?? '',
        entry.projectName ?? '',
        '',
        '',
        '',
        ev.occurredAt.slice(11, 16),
        ev.app ?? '',
        ev.title ?? '',
        ev.site ?? '',
        ev.classifiedClientName ?? '',
        ev.classifiedProjectName ?? '',
      ]);
    }
  }

  for (const ev of diary.unattributedEventItems) {
    rows.push([
      'Unattributed Event',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      ev.occurredAt.slice(11, 16),
      ev.app ?? '',
      ev.title ?? '',
      ev.site ?? '',
      ev.classifiedClientName ?? '',
      ev.classifiedProjectName ?? '',
    ]);
  }

  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `day-diary-${diary.employeeName.replace(/\s+/g, '-').toLowerCase()}-${diary.date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function EventBreakdownPanel({ breakdown }: { breakdown: AppTitleGroup[] }) {
  const totalEvents = breakdown.reduce((s, g) => s + g.total, 0);
  const [open, setOpen] = useState(false);
  if (totalEvents === 0) return null;

  return (
    <>
      <button className={styles.blockEventsToggle} onClick={() => setOpen((v) => !v)}>
        <span>{open ? '▾' : '▸'}</span>
        <span>
          {totalEvents} {totalEvents === 1 ? 'event' : 'events'}
        </span>
      </button>
      {open && (
        <div className={styles.blockEventsPanel}>
          {breakdown.map((group) => (
            <div key={group.app} className={styles.appGroup}>
              <div className={styles.appGroupHeader}>
                <span>{group.app}</span>
                <span className={styles.appGroupCount}>{group.total}</span>
              </div>
              {group.titles.map((t) => (
                <div key={t.title} className={styles.appGroupRow}>
                  <span className={styles.appGroupTitle}>{t.title}</span>
                  {t.categories.length > 0 && (
                    <span className={styles.appGroupCategories}>
                      {t.categories.map((c) => (
                        <span
                          key={`${c.client ?? ''}/${c.project ?? ''}`}
                          className={styles.categoryChip}
                        >
                          {c.client ?? ''}
                          {c.client && c.project ? ' / ' : ''}
                          {c.project ?? ''}
                        </span>
                      ))}
                    </span>
                  )}
                  <span className={styles.appGroupCount}>{t.count}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function EntryCard({ entry }: { entry: DayDiaryEntry }) {
  const distinctCategories = countDistinctCategories(entry.eventItems ?? []);
  const duration = formatDuration(entry.startsAt, entry.endsAt);
  const breakdown = groupEventItems(entry.eventItems ?? []);

  return (
    <div className={styles.entryCard}>
      <div className={styles.entryHeader}>
        <h2 className={styles.entryTitle}>{entry.title}</h2>
        <div className={styles.entryMeta}>
          <span className={styles.statBadge}>{duration}</span>
          <span className={styles.statBadge}>
            {entry.eventCount} {entry.eventCount === 1 ? 'event' : 'events'}
          </span>
          {distinctCategories > 0 && (
            <span className={styles.statBadge}>
              {distinctCategories} {distinctCategories === 1 ? 'category' : 'categories'}
            </span>
          )}
          {entry.projectId && (
            <span className={styles.projectBadge}>
              {entry.clientName ? `${entry.clientName} / ` : ''}
              {entry.projectName}
            </span>
          )}
        </div>
      </div>
      <p className={styles.entryDescription}>{entry.description}</p>
      <EventBreakdownPanel breakdown={breakdown} />
      <span className={styles.entryTimeRange}>{formatTimeRange(entry.startsAt, entry.endsAt)}</span>
    </div>
  );
}

function UnattributedPanel({ items }: { items: UnattributedEventItem[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.unattributedPanel}>
      <button className={styles.unattributedToggle} onClick={() => setOpen((v) => !v)}>
        <span>{open ? '▾' : '▸'}</span>
        <span>
          {items.length} unattributed {items.length === 1 ? 'event' : 'events'}
        </span>
      </button>
      {open && (
        <table className={styles.unattributedTable}>
          <thead>
            <tr>
              <th>Time (UTC)</th>
              <th>App</th>
              <th>Title / Site</th>
              <th>Client / Project</th>
            </tr>
          </thead>
          <tbody>
            {items.map((ev) => (
              <tr key={ev.id}>
                <td className={styles.unattributedTime}>{ev.occurredAt.slice(11, 16)}</td>
                <td className={styles.unattributedApp}>{ev.app ?? '—'}</td>
                <td className={styles.unattributedTitle}>{ev.title ?? ev.site ?? '—'}</td>
                <td className={styles.unattributedCategory}>
                  {ev.classifiedClientName
                    ? `${ev.classifiedClientName}${ev.classifiedProjectName ? ` / ${ev.classifiedProjectName}` : ''}`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SummaryBlockCard({
  block,
  entries,
}: {
  block: DiarySummaryBlock;
  entries: DayDiaryEntry[];
}) {
  const breakdown = buildAppBreakdown(entries, block.entryIds);

  return (
    <div className={styles.summaryBlock}>
      <div className={styles.summaryBlockHeader}>
        <span className={styles.summaryBlockLabel}>{block.label}</span>
        <span className={styles.summaryBlockDuration}>{fmtMinutes(block.totalMinutes)}</span>
      </div>
      <p className={styles.summaryBlockNarrative}>{block.narrative}</p>
      <EventBreakdownPanel breakdown={breakdown} />
    </div>
  );
}

function SummaryView({ summary, entries }: { summary: DiarySummary; entries: DayDiaryEntry[] }) {
  return (
    <div className={styles.summaryView}>
      {summary.clients.map((client) => (
        <div key={client.clientName} className={styles.summaryClient}>
          <h2 className={styles.summaryClientHeader}>
            {client.clientName}
            <span className={styles.summaryClientDuration}>{fmtMinutes(client.totalMinutes)}</span>
          </h2>
          {client.blocks.map((block, i) => (
            <SummaryBlockCard key={i} block={block} entries={entries} />
          ))}
        </div>
      ))}
      <p className={styles.mindShare}>{summary.mindShare}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DayDiaryPage() {
  const { data: employees, isLoading: employeesLoading } = useEmployees();
  const { data: diary, isLoading, isGenerating, error, generate, lookup } = useDayDiary();
  const v2 = useDayDiaryV2();
  const { data: orgSettings } = useOrgSettings();
  const tz = orgSettings?.timezone ?? 'UTC';

  // §6.13 Phase 1 — the screenshot-view gate evaluates to true for every org
  // member (requireRole('member') + requireOrgScope). Any member who reached
  // this page passes the existing gate, so the prop is the org-member default.
  // Phase 2 (roadmap, not this task) swaps this input to
  // `viewerIsSelfOrOrgAdmin` — same prop, stricter feeder.
  const canViewScreenshots = true;

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [activeTab, setActiveTab] = useState<'summary' | 'timeline'>('summary');

  const employeeOptions = employees.map((e) => ({ value: e.id, label: e.name }));

  // Auto-lookup whenever employee+date are both set. Use a ref to skip the
  // initial mount fire when both fields are empty.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (selectedEmployeeId && selectedDate) {
      void lookup(selectedEmployeeId, selectedDate);
      void v2.lookup(selectedEmployeeId, selectedDate);
    }
  }, [selectedEmployeeId, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps -- lookup and generate are stable useCallback refs; adding them would cause an infinite re-render loop

  const handleGenerate = () => {
    if (!selectedEmployeeId || !selectedDate) return;
    void generate(selectedEmployeeId, selectedDate, hasExistingDiary);
    void v2.generate(selectedEmployeeId, selectedDate, hasExistingDiary);
  };

  const canGenerate = !!selectedEmployeeId && !!selectedDate && !isLoading && !isGenerating;
  const hasExistingDiary = !!diary;

  return (
    <>
      <PageHeader
        title="Day Diary"
        subtitle="AI summary of an employee's workday with client/project attribution."
      />

      <div className={styles.form}>
        <Select
          label="Employee"
          value={selectedEmployeeId}
          options={employeeOptions}
          placeholder={employeesLoading ? 'Loading…' : 'Select employee'}
          onChange={(v) => setSelectedEmployeeId(v)}
          size="md"
        />

        <div className={styles.dateField}>
          <label className={styles.dateLabel} htmlFor="diary-date">
            Date
          </label>
          <input
            id="diary-date"
            type="date"
            className={styles.dateInput}
            value={selectedDate}
            max={todayIso()}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <Button variant="primary" onClick={handleGenerate} disabled={!canGenerate}>
          {isGenerating
            ? 'Generating…'
            : isLoading
              ? 'Loading…'
              : hasExistingDiary
                ? 'Regenerate'
                : 'Generate'}
        </Button>

        {diary && (
          <Button variant="secondary" onClick={() => exportToCsv(diary)}>
            Export CSV
          </Button>
        )}
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}
      {v2.error && <div className={styles.errorMessage}>{v2.error}</div>}

      {/* ── Day Diary v2 surface — progress (AC1) + grouping (AC2) ─────────── */}
      {v2.status !== null && (
        <div className={styles.v2Surface} data-testid="day-diary-v2">
          {!v2.isComplete ? (
            <div className={styles.v2Progress} data-testid="day-diary-v2-progress">
              <h2 className={styles.v2Heading}>Preparing your diary…</h2>
              <ProjectBatchStatusList batches={v2.batches} />
            </div>
          ) : (
            <div className={styles.v2Complete}>
              {v2.batches
                .filter((b) => b.projectId !== null)
                .map((b) => (
                  <section
                    key={b.id}
                    className={styles.v2ProjectSection}
                    data-testid="v2-project-section"
                  >
                    <h2 className={styles.v2Heading}>
                      {b.clientName ? `${b.clientName} / ` : ''}
                      {b.projectName ?? 'Project'}
                    </h2>
                    <p className={styles.v2SectionMeta}>
                      {b.eventCount} {b.eventCount === 1 ? 'event' : 'events'}
                    </p>
                  </section>
                ))}
              {v2.batches.some((b) => b.projectId === null) && (
                <ReclamationSuggestionsPanel
                  suggestions={v2.suggestions}
                  canViewScreenshots={canViewScreenshots}
                  tz={tz}
                  busyId={v2.busyId}
                  onAccept={(s) => void v2.accept(s)}
                  onDismiss={(s) => void v2.dismiss(s)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {diary && (
        <>
          <div className={styles.summary}>
            <span>
              <span className={styles.summaryHighlight}>{formatDayFull(diary.date)}</span>
              {' — '}
              <span className={styles.summaryHighlight}>{diary.employeeName}</span>
            </span>
            <span className={styles.summaryDot}>·</span>
            <span>
              <span className={styles.summaryHighlight}>{diary.totalEvents}</span>
              {' total events'}
            </span>
            <span className={styles.summaryDot}>·</span>
            <span>
              <span className={styles.summaryHighlight}>{diary.workEventCount}</span>
              {' attributed across '}
              <span className={styles.summaryHighlight}>{diary.entries.length}</span>
              {diary.entries.length === 1 ? ' entry' : ' entries'}
            </span>
            {diary.unattributedEvents > 0 && (
              <>
                <span className={styles.summaryDot}>·</span>
                <span className={styles.summaryUnattributed}>
                  {diary.unattributedEvents} unattributed
                </span>
              </>
            )}
            {diary.generatedAt && (
              <>
                <span className={styles.summaryDot}>·</span>
                <span className={styles.summaryMuted}>
                  generated {diary.generatedAt.slice(0, 10)} {diary.generatedAt.slice(11, 16)} UTC
                </span>
              </>
            )}
          </div>

          {diary.rawSummary && (
            <div className={styles.tabStrip}>
              <Button
                variant="chip"
                onClick={() => setActiveTab('summary')}
                aria-pressed={activeTab === 'summary'}
              >
                Summary
              </Button>
              <Button
                variant="chip"
                onClick={() => setActiveTab('timeline')}
                aria-pressed={activeTab === 'timeline'}
              >
                Timeline
              </Button>
            </div>
          )}

          {diary.rawSummary && activeTab === 'summary' ? (
            <SummaryView summary={diary.rawSummary} entries={diary.entries} />
          ) : (
            <>
              {diary.entries.length === 0 ? (
                <div className={styles.emptyState}>
                  No work blocks were identified for this day.
                </div>
              ) : (
                <div className={styles.entriesList}>
                  {diary.entries.map((entry) => (
                    <EntryCard key={entry.id} entry={entry} />
                  ))}
                </div>
              )}

              {(diary.unattributedEventItems?.length ?? 0) > 0 && (
                <UnattributedPanel items={diary.unattributedEventItems ?? []} />
              )}
            </>
          )}
        </>
      )}

      {isGenerating && !diary && (
        <div className={styles.emptyState}>Generating diary, this may take a few minutes…</div>
      )}

      {!diary && !isLoading && !isGenerating && !error && (
        <EmptyState
          title={
            selectedEmployeeId && selectedDate ? 'No diary for this day' : 'No diary generated yet'
          }
          description={
            selectedEmployeeId && selectedDate
              ? 'No diary has been generated for this employee and date. Click Generate to create one.'
              : 'Select an employee and date above, then click Generate.'
          }
        />
      )}
    </>
  );
}
